<template>
  <b-card class="page-view-table page-view-table-joint">
    <div
      v-if="onProgress"
      class="data-loading d-flex justify-content-center"
    >
      <b-spinner
        class="mt-1"
        label="Loading"
      />
    </div>
    <!-- tables -->
    <vue-good-table
      :columns="columns"
      :rows="rows"
      :rtl="direction"
      style-class="vgt-table"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
        searchFn: searchTable,
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength
      }"
      @on-sort-change="onSortChange"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <!-- Column: index -->
        <span
          v-if="props.column.field === 'index'"
          class="text-nowrap"
        >
          <span class="text-nowrap">{{ props.row.originalIndex + 1 }}</span>
          <feather-icon
            v-if="props.row.breadcrumb == 'Gallery'"
            v-b-tooltip.hover
            icon="ImageIcon"
            class="mr-25 d-block"
            title="Gallery"
          />
        </span>
        <!-- Column: pageViews -->
        <span
          v-else-if="props.column.field === 'pvCount'"
          class="page-view-item"
        >
          <span
            v-if="props.row.pvCount"
            class="page-view-count font-weight-bold"
          >{{ props.row.pvCount.toLocaleString() }}</span>
        </span>

        <!-- Column: articles -->
        <span v-else-if="props.column.field === 'pageTitle'">
          <span class="article-title">
            <a
              class="d-inline-block page-title-link"
              v-html="props.row.pageTitle"
              :href="props.row.pageUrl"
              target="_blank"
            />
          </span>
        </span>
        <span
          v-else
        >
          <span
            v-if="props.row.avgScrollPercentage"
            class="avg-page-duration"
          >
            <b-badge
              v-b-tooltip.hover
              variant="light"
              title="Average Scroll Percentage"
            >
              <span>{{ props.row.avgScrollPercentage }}%</span>
            </b-badge>
          </span>
        </span>
        </span>
        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

      <!-- pagination -->
      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <!-- pagination -->
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value)=>props.pageChanged({currentPage:value})"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </b-card>
</template>

<script>
import {
    BCard, BPagination, BFormGroup, BFormInput, BFormSelect, BBadge, VBTooltip, BFormCheckbox, BSpinner,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import 'prismjs'
import 'prismjs/themes/prism-tomorrow.css'
import store from '@/store'
import { getCoreApiByObjectId } from '@/coreApi/coreApiHelper'

// eslint-disable-next-line import/no-extraneous-dependencies
const moment = require('moment')

export default {
    components: {
        BCard,
        VueGoodTable,
        BPagination,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BBadge,
        BFormCheckbox,
        BSpinner,
    },
    directives: {
        'b-tooltip': VBTooltip,
    },
    props: {
        pageViews: {
            type: Object,
            required: true,
        },
        onProgress: {
            type: Boolean,
            default: true,
        },
        byContentFilter: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pageLength: 10,
            dir: false,
            pages: ['100', '200'],
            columns: [

                {
                    label: '',
                    field: 'pvCount',
                    type: 'number',
                    sortable: false,
                    tdClass: 'text-left page-view-column',
                },
                {
                    label: 'Articles',
                    field: 'pageTitle',
                    sortable: true,
                    thClass: 'page-title-column-th',
                    tdClass: 'text-left align-top page-title-column',
                },
                {
                    label: 'Zones',
                    field: 'zones',
                    sortable: false,
                    thClass: 'zone-column-mobile-th',
                    tdClass: 'zone-column-mobile',
                },
            ],
            rows: [],
            allRows: [],
            searchTerm: '',
            status: [{
                1: 'Current', 2: 'Professional', 3: 'Rejected', 4: 'Resigned', 5: 'Applied',
            },
            {
                1: 'light-primary', 2: 'light-success', 3: 'light-danger', 4: 'light-warning', 5: 'light-info',
            }],
            filterShowTodayPages: false,
            filterShowAllPages: !this.byContentFilter,
            showContentFilter: true,
        }
    },
    computed: {
        direction() {
            if (store.state.appConfig.isRTL) {
                // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                this.dir = true
                return this.dir
            }
            // eslint-disable-next-line vue/no-side-effects-in-computed-properties
            this.dir = false
            return this.dir
        },
    },
    watch: {
        byContentFilter() {
            this.filterShowAllPages = !this.byContentFilter
        },
        pageViews() {
            this.allRows = this.tableData()
            this.changeRows()
        },
    },
    created() {
        this.allRows = this.tableData()
        this.changeRows()
    },
    methods: {
        tableData() {
            const object = getCoreApiByObjectId(this.pageViews, 'pageViews')
            const tableData = []
            if (
                object
          && 'data' in object
            ) {
                object.data.forEach(objectItem => {
                    const item = { ...objectItem }

                    let pageUrlPath = null
                    if (item.pageUrl) {
                        try {
                            const pageUrl = new URL(item.pageUrl)
                            pageUrlPath = pageUrl.pathname
                            if (pageUrlPath) {
                                pageUrlPath = pageUrlPath.replaceAll('/', '').trim()
                            }
                        } catch (e) {
                            pageUrlPath = null
                        }
                    }
                    if (
                        (pageUrlPath !== '')
                        && (item.headline && item.headline.trim() !== '')
                        && ['Content', 'Gallery'].indexOf(item.breadcrumb) > -1
                    ) {
                        item.pageTitle = item.headline
                    }

                    if (item.pageUrl && item.pageUrl.indexOf('/error') > -1) {
                        item.pageTitle = `<span style="color: red;">(Error)</span> ${item.pageTitle}`
                    }

                    if (!item.pageTitle) {
                        item.pageTitle = item.pageUrl
                    }

                    if (item.avgPageDuration) {
                        item.avgPageDuration = moment.utc(item.avgPageDuration * 1000).format('mm:ss')
                    }

                    if (item.datePublished) {
                        item.datePublished = moment(new Date(item.datePublished * 1000)).calendar(null, {
                            sameDay: '[Today:] HH:mm',
                            nextDay: '[Tomorrow:] HH:mm',
                            nextWeek: 'dddd HH:mm',
                            lastDay: '[Yesterday:] HH:mm',
                            lastWeek: '[Last:] dddd HH:mm',
                            sameElse: 'L',
                        })
                    } else {
                        item.datePublished = null
                    }

                    if (item.dateModified) {
                        item.dateModified = moment(new Date(item.dateModified * 1000)).calendar(null, {
                            sameDay: '[Today:] HH:mm',
                            nextDay: '[Tomorrow:] HH:mm',
                            nextWeek: 'dddd HH:mm',
                            lastDay: '[Yesterday:] HH:mm',
                            lastWeek: '[Last:] dddd HH:mm',
                            sameElse: 'L',
                        })
                    } else {
                        item.dateModified = null
                    }

                    tableData.push(item)
                })
            }

            return tableData
        },
        filterShowTodayPagesChanged() {
            this.filterShowAllPages = false
            this.changeRows()
        },
        filterShowAllPagesChanged() {
            this.filterShowTodayPages = false
            this.changeRows()
        },
        changeRows() {
            let rows = this.allRows
            if (!this.filterShowAllPages) {
                rows = this.allRows.filter(obj => ['Content', 'Gallery'].indexOf(obj.breadcrumb) > -1)
            }

            if (this.filterShowTodayPages) {
                rows = rows.filter(obj => obj.datePublished && obj.datePublished.indexOf('Today') > -1)
            }

            this.rows = rows
        },
        handleSearch(searching) {
            if (searching) {
                this.showContentFilter = false
                this.rows = this.allRows
            } else {
                this.showContentFilter = true
                this.changeRows()
            }
        },
        handleChangePage(page) {
        },
        handlePageChange(active) {
        },
        onSortChange(params) {
        },
        readabilityClassName(value) {
            let className = 'success'
            if (value <= 20) {
                className = 'danger'
            } else if (value <= 40) {
                className = 'warning'
            } else if (value <= 60) {
                className = 'info'
            } else if (value <= 80) {
                className = 'primary'
            } else {
                className = 'success'
            }

            return className
        },
        searchTable(row, col, cellValue, searchTerm) {
            return !searchTerm ? true : (row.pageTitle && row.pageTitle.includes(searchTerm))
                || (row.pageUrl && row.pageUrl.includes(searchTerm))
                || (row.headline && row.headline.includes(searchTerm))
                || (row.author && row.author.includes(searchTerm))
                || (row.info && row.info.includes(searchTerm))
                || (row.pageDomain && row.pageDomain.includes(searchTerm))
        },
        isMobile() {
            return window.innerWidth < 768
        },
        getReferersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getOriginReferrersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getRefererUtmsSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getOriginUtmsReferersSlice(referers) {
            return referers.slice(0, this.isMobile() ? 3 : 8)
        },
        getReferrerCapitalizeClass(referer) {
            return !(referer.includes('www') || referer.includes('com')) ? 'text-transform-capitalize' : ''
        },
    },
}
</script>

<style lang="scss" >
@import '~@core/scss/vue/libs/vue-good-table.scss';
</style>
